import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import {
  selectPropLibraryTargetPosition,
  setIsPropLibraryOpen,
} from "../../app/slices/GlobalSlice";
import {
  addSelectedPropAsUsedProp,
  addSituationAsUsedProp,
  selectPropCategories,
  selectPropFilter,
  selectProps,
  selectSelectedPropContent,
  selectSelectedPropId,
  selectSelectedPropMetadata,
  setPropFilter,
  setSelectedProp,
} from "../../app/slices/PropLibrarySlice";
import {
  selectSelectedSituationContent,
  selectSelectedSituationId,
  selectSelectedSituationMetadata,
  selectSituations,
  setSelectedSituation,
} from "../../app/slices/SituationSlice";
import {
  addPropPreset,
  addPropStateToSelectedTake,
} from "../../app/slices/TrainingSlice";
import { registerPropLocalizedKeys } from "../../app/slices/TrainingLocalizationSlice";
import {
  BrowseDialogViewMode,
  selectBrowseDialogViewMode,
  setBrowseDialogViewMode,
} from "../../app/slices/GlobalSlice";
import PiBrowseLayout from "../../components/PiBrowseLayout";
import {
  browseListboxId,
  browseOptionId,
} from "../../components/browseListboxIds";
import { SituationMetadata } from "../../dto/SituationMetadata";
import { TranslatedPropDTO } from "../../dto/TranslatedPropDTO";
import { GetNewGuid } from "../../helpers/GuidHelper";
import useFilterableList from "../../hooks/useFilterableList";
import useSituationHelper from "../../hooks/SituationHelper.hook";
import { PropType } from "../../models/Prop";
import useSituationPagination from "../situation_library/SituationPagination.hook";
import UsePropLibrary from "./PropLibrary.hooks";
import usePropLibraryPagination from "./PropLibraryPagination.hooks";
import PropLibraryDetailsPane from "./PropLibraryDetailsPane";
import { PropLibraryDescriptionProvider } from "./PropLibraryDescription";
import PropLibraryRail from "./PropLibraryRail";
import PropLibraryResults, {
  DEFAULT_PROP_COLUMNS,
  isUnloadedProp,
} from "./PropLibraryResults";
import PropLibraryScopeSwitcher, {
  PropLibraryScope,
} from "./PropLibraryScopeSwitcher";

interface PropLibraryBodyProps {
  onClose: () => void;
}

const DIALOG = "proplibrary";

const EMPTY_SEARCH_STRINGS: string[] = [];
const getSearchStrings = () => EMPTY_SEARCH_STRINGS;

/**
 * Everything inside the prop library dialog. Search is server-backed — the
 * query lives in the slice and reloads the first page — so the keyboard hook
 * runs with no client-side filter: it only navigates the sparse paged array.
 */
export default function PropLibraryBody(props: PropLibraryBodyProps) {
  const { onClose } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [scope, setScope] = useState<PropLibraryScope>("props");
  const [tileColumns, setTileColumns] = useState(DEFAULT_PROP_COLUMNS);

  const filter = useSelector(selectPropFilter);
  const viewMode = useSelector(selectBrowseDialogViewMode);
  const categories = useSelector(selectPropCategories);

  const pagedProps = useSelector(selectProps);
  const situations = useSelector(selectSituations);
  const items = scope === "props" ? pagedProps : situations;

  const selectedPropId = useSelector(selectSelectedPropId);
  const selectedSituationId = useSelector(selectSelectedSituationId);
  const selectedId = scope === "props" ? selectedPropId : selectedSituationId;

  const propContent = useSelector(selectSelectedPropContent);
  const propMetadata = useSelector(selectSelectedPropMetadata);
  const situationContent = useSelector(selectSelectedSituationContent);
  const situationMetadata = useSelector(selectSelectedSituationMetadata);
  const targetPosition = useSelector(selectPropLibraryTargetPosition);

  const propPagination = usePropLibraryPagination();
  const situationPagination = useSituationPagination();
  const { loadCategories, loadInteractions, loadPropContent, loadSituationContent } =
    UsePropLibrary();
  const { addDynamicProp } = useSituationHelper();

  // The category/interaction lookups feed the rail and the chips everywhere.
  useEffect(() => {
    if (categories.length === 0) {
      loadCategories();
      loadInteractions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The add needs the selection's content and metadata, which load after the
  // highlight settles. Until they match the selection, the primary stays
  // disabled and Enter keeps the dialog open.
  const contentReady =
    scope === "props"
      ? !!propContent && propContent.id === selectedId && !!propMetadata
      : !!situationContent &&
        situationContent.id === selectedId &&
        !!situationMetadata;

  const loadContentDebounced = useDebouncedCallback((prop: TranslatedPropDTO) => {
    if (scope === "props") loadPropContent(prop.id);
    else loadSituationContent(prop.id);
  }, 250);

  const selectProp = useCallback(
    (prop: TranslatedPropDTO) => {
      if (isUnloadedProp(prop)) return;

      if (scope === "props") dispatch(setSelectedProp(prop.id));
      else dispatch(setSelectedSituation(prop.id));
      loadContentDebounced(prop);
    },
    [scope, dispatch, loadContentDebounced]
  );

  const addProp = useCallback(() => {
    const id = GetNewGuid();
    dispatch(
      addPropPreset({
        id,
        propId: propContent!.id,
        name: propContent!.name,
        prefabName: propMetadata!.prefab_name,
        propType: PropType.Default,
        hideInPropLibrary: propContent!.hideInPropLibrary ?? false,
      })
    );

    // if there is a target position, add the prop to the take
    if (targetPosition) {
      dispatch(
        addPropStateToSelectedTake({
          instanceId: id,
          positionId: targetPosition,
        })
      );
    }

    dispatch(addSelectedPropAsUsedProp());

    if (propMetadata!.localizedKeys?.length) {
      dispatch(registerPropLocalizedKeys(propMetadata!.localizedKeys));
    }
  }, [dispatch, propContent, propMetadata, targetPosition]);

  const addSituation = useCallback(() => {
    const newSituationProp = {
      id: GetNewGuid(),
      propId: situationContent!.id,
      name: situationContent!.name,
      prefabName: situationMetadata!.prefab_name,
      propType: PropType.Situation,
      hideInPropLibrary: situationContent!.hideInPropLibrary ?? false,
    };
    dispatch(addPropPreset(newSituationProp));

    // add all dynamic props
    const metadata = situationMetadata as SituationMetadata;
    metadata.prop_positions.forEach((position) => {
      if (position.dynamicPropId)
        addDynamicProp(position.dynamicPropId, newSituationProp.id, position.id);
    });

    dispatch(
      addSituationAsUsedProp({
        id: situationContent!.id,
        propContent: situationContent!,
        metadata: situationMetadata!,
      })
    );

    if (situationMetadata!.localizedKeys?.length) {
      dispatch(registerPropLocalizedKeys(situationMetadata!.localizedKeys));
    }
  }, [dispatch, situationContent, situationMetadata, addDynamicProp]);

  // Both call sites drive `open` from GlobalSlice, so closing through the
  // slice (as the old details pane did) and the onClose prop reach the same
  // place; the prop keeps the API honest for any future caller.
  const confirmAdd = useCallback((): boolean | void => {
    if (!contentReady) return false;

    try {
      if (scope === "props") addProp();
      else addSituation();
    } finally {
      dispatch(setIsPropLibraryOpen({ open: false, positionId: "" }));
      onClose();
    }
  }, [contentReady, scope, addProp, addSituation, dispatch, onClose]);

  const list = useFilterableList<TranslatedPropDTO>({
    items,
    getSearchStrings,
    columns: viewMode === "tile" ? tileColumns : 1,
    initiallyOpen: true,
    focusSearchOnSlash: true,
    onSelect: (prop) => {
      // Enter on an unloaded placeholder or before content is ready selects
      // and keeps the dialog open rather than silently closing. When the add
      // does run, confirmAdd owns the close (it is also the footer's and the
      // double-click's path) — so the hook's own close is always vetoed.
      selectProp(prop);
      if (!isUnloadedProp(prop) && contentReady) confirmAdd();
      return false;
    },
    onClose,
  });

  const { selectedIndex, setSelectedIndex, setOpen } = list;

  useEffect(() => {
    setOpen(true);
    return () => setOpen(false);
  }, [setOpen]);

  // Arrow movement mirrors into the slice selection, like the picker.
  const highlighted = items[selectedIndex];
  useEffect(() => {
    if (isUnloadedProp(highlighted)) return;
    if (highlighted.id === selectedId) return;

    selectProp(highlighted);
  }, [highlighted, selectedId, selectProp]);

  // Scope and search changes restart at the top.
  const onScopeChange = useCallback(
    (next: PropLibraryScope) => {
      setScope(next);
      setSelectedIndex(0);
    },
    [setSelectedIndex]
  );

  const onSearchChange = useCallback(
    (value: string) => {
      dispatch(setPropFilter(value));
      setSelectedIndex(0);
    },
    [dispatch, setSelectedIndex]
  );

  const onViewModeChange = useCallback(
    (mode: BrowseDialogViewMode) => {
      dispatch(setBrowseDialogViewMode(mode));
    },
    [dispatch]
  );

  const onRangeRendered =
    scope === "props" ? propPagination.onRerender : situationPagination.onRerender;

  return (
    <PropLibraryDescriptionProvider scope={scope}>
      <PiBrowseLayout
        searchValue={filter}
        onSearchChange={onSearchChange}
        searchPlaceholder={t("prop_library_ui.search_placeholder")}
        searchInputRef={list.searchInputRef}
        // The query goes to the server, so it waits longer than the shell's
        // default for a client-side filter.
        searchDebounceMs={300}
        searchFieldId="proplibrary-search"
        listboxId={browseListboxId(DIALOG)}
        activeDescendantId={browseOptionId(DIALOG, selectedIndex)}
        scopeSwitcher={
          <PropLibraryScopeSwitcher value={scope} onChange={onScopeChange} />
        }
        viewMode={viewMode}
        onViewModeChange={onViewModeChange}
        rail={<PropLibraryRail />}
        results={
          <PropLibraryResults
            items={items}
            viewMode={viewMode}
            selectedIndex={selectedIndex}
            onSelectIndex={(index) => {
              setSelectedIndex(index);
              const prop = items[index];
              if (prop) selectProp(prop);
            }}
            onConfirmIndex={(index) => {
              const prop = items[index];
              if (prop) {
                selectProp(prop);
                confirmAdd();
              }
            }}
            onColumnsChange={setTileColumns}
            listboxId={browseListboxId(DIALOG)}
            optionIdFor={(index) => browseOptionId(DIALOG, index)}
            onRangeRendered={onRangeRendered}
          />
        }
        details={
          <PropLibraryDetailsPane
            prop={isUnloadedProp(highlighted) ? undefined : highlighted}
            content={scope === "props" ? propContent : situationContent}
          />
        }
        primaryLabel={t("prop_library_ui.add_prop") ?? ""}
        primaryDisabled={!contentReady}
        onPrimaryAction={confirmAdd}
      />
    </PropLibraryDescriptionProvider>
  );
}
